/**
 * Session Timer Component
 * Displays countdown timer for checkout session expiration
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';

interface SessionTimerProps {
  expiresAt: string;
  onExpired?: () => void;
}

export default function SessionTimer({ expiresAt, onExpired }: SessionTimerProps) {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState<{
    minutes: number;
    seconds: number;
    isLow: boolean;
  } | null>(null);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft({ minutes: 0, seconds: 0, isLow: true });
        onExpired?.();
        return;
      }

      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      const isLow = minutes < 5; // Warning color if less than 5 minutes

      setTimeLeft({ minutes, seconds, isLow });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [expiresAt, onExpired]);

  if (!timeLeft) {
    return null;
  }

  const formattedTime = `${String(timeLeft.minutes).padStart(2, '0')}:${String(
    timeLeft.seconds
  ).padStart(2, '0')}`;

  return (
    <div className="flex items-center justify-center gap-2">
      <Clock
        className={`h-4 w-4 ${
          timeLeft.isLow
            ? 'text-yellow-500'
            : 'text-blue-500'
        }`}
      />
      <span
        className={`text-sm font-medium ${
          timeLeft.isLow
            ? 'text-yellow-600 dark:text-yellow-400'
            : 'text-blue-600 dark:text-blue-400'
        }`}
      >
        {t('checkout.sessionTimer')}: {formattedTime}
      </span>
    </div>
  );
}
